"use client";

/**
 * HtmlViewerSidebar — desktop right-sidebar for a sandboxed HTML document.
 */

import { motion } from "motion/react";
import { FileCode, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { HtmlViewer } from "@/components/html-viewer";
import { HtmlViewerToolbar } from "@/components/html-viewer-toolbar";
import { cn } from "@/lib/utils";

export interface HtmlViewerSidebarProps {
  content: string | null;
  loading: boolean;
  error: string | null;
  fileName: string;
  onClose: () => void;
  onRetry?: () => void;
  /** True briefly after a background refresh delivers new content. */
  refreshed?: boolean;
  /** Sidebar width in pixels (controlled by useResizableWidth). */
  width: number;
  /** Signed file URL for the "open in new tab" pop-out. */
  fileUrl?: string;
  sourceUrl?: string;
  /** Called when the user wants to open the viewer in a new tab. */
  onOpenInNewWindow?: () => void;
  /** Drag-handle pointer handlers from useResizableWidth. */
  onResizePointerDown: (e: React.PointerEvent) => void;
  onResizePointerMove: (e: React.PointerEvent) => void;
  onResizePointerUp: (e: React.PointerEvent) => void;
  onResizeDoubleClick: () => void;
}

export function HtmlViewerSidebar({
  content,
  loading,
  error,
  fileName,
  onClose,
  onRetry,
  refreshed,
  width,
  fileUrl,
  sourceUrl,
  onOpenInNewWindow,
  onResizePointerDown,
  onResizePointerMove,
  onResizePointerUp,
  onResizeDoubleClick,
}: HtmlViewerSidebarProps) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className={cn(
        "relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background",
      )}
      style={{ width }}
      data-testid="html-viewer-sidebar"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("viewer.dragToResize")}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onDoubleClick={onResizeDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 cursor-col-resize touch-none select-none transition-colors hover:bg-primary/30 active:bg-primary/40"
        data-testid="html-viewer-resize-handle"
      />

      <div className="flex min-h-[53px] items-center justify-between gap-2 border-b px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <FileCode className="size-4 shrink-0 text-muted-foreground" />
          <span
            className="truncate text-sm font-semibold"
            title={fileName}
            data-testid="html-viewer-filename"
          >
            {fileName}
          </span>
          {refreshed && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-blue-500"
              title={t("viewer.contentUpdated")}
              data-testid="html-viewer-refreshed-indicator"
            />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <HtmlViewerToolbar
            content={content}
            fileName={fileName}
            fileUrl={fileUrl}
            sourceUrl={sourceUrl}
            onOpenInNewWindow={onOpenInNewWindow}
          />
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <Spinner className="size-5" />
            <p className="text-sm">{t("common:status.loadingDocument")}</p>
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
            <p className="text-sm text-destructive">{error}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t("common:actions.retry")}
              </Button>
            )}
          </div>
        ) : content != null ? (
          <HtmlViewer content={content} fileName={fileName} />
        ) : null}
      </div>
    </motion.div>
  );
}
